import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowLeft, faArrowRight, faPlay, faQuoteLeft, faSearch,
} from '@fortawesome/free-solid-svg-icons';
import { getConfig } from '@edx/frontend-platform';
import { useIntl } from '@edx/frontend-platform/i18n';
import {
  Button, Container, useToggle,
} from '@openedx/paragon';

import HomeCoursesListSlot from '@src/plugin-slots/HomeCoursesListSlot';
import { HomePromoVideoModalSlot } from '@src/plugin-slots/HomePromoVideoSlots';
import { ROUTES } from '@src/routes';

import {
  heroContent,
  partnerItems,
  serviceItems,
  stepItems,
  testimonialItems,
} from './content';
import messages from './messages';

const SectionHeading = ({
  eyebrow,
  title,
}: {
  eyebrow: string;
  title: string;
}) => (
  <div className="jiltarjih-section__title-block">
    <p className="jiltarjih-section__eyebrow">{eyebrow}</p>
    <h2>{title}</h2>
  </div>
);

export const HomeHeroSection = () => {
  const intl = useIntl();
  const navigate = useNavigate();
  const [searchValue, setSearchValue] = useState('');
  const [isVideoOpen, openVideo, closeVideo] = useToggle(false);
  const videoId = getConfig().HOMEPAGE_PROMO_VIDEO_YOUTUBE_ID || '';
  const showSearch = getConfig().ENABLE_COURSE_DISCOVERY;
  const showVideoPreview = Boolean(videoId);

  const handleSearch = () => navigate(`${ROUTES.COURSES}?search_query=${searchValue}`);
  const handleSearchSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    handleSearch();
  };

  return (
    <section
      className="jiltarjih-hero"
      data-testid="jiltarjih-hero"
      style={{ '--jiltarjih-hero-image': `url(${heroContent.background})` } as React.CSSProperties}
    >
      <Container size="xl" className="jiltarjih-hero__inner">
        <div className="jiltarjih-hero__layout">
          <div className="jiltarjih-hero__content">
            <h1>{intl.formatMessage(heroContent.title)}</h1>
            <p className="jiltarjih-hero__lead">{intl.formatMessage(heroContent.lead)}</p>
            {showSearch && (
              <form className="jiltarjih-hero__search" onSubmit={handleSearchSubmit}>
                <input
                  type="search"
                  className="jiltarjih-hero__search-input"
                  placeholder={intl.formatMessage(heroContent.searchPlaceholder)}
                  value={searchValue}
                  onChange={(event) => setSearchValue(event.target.value)}
                />
                <button
                  type="submit"
                  className="jiltarjih-hero__search-button"
                  aria-label={intl.formatMessage(heroContent.searchPlaceholder)}
                >
                  <FontAwesomeIcon icon={faSearch} />
                </button>
              </form>
            )}
          </div>
          {showVideoPreview && (
            <div className="jiltarjih-hero__media">
              <button
                type="button"
                className="jiltarjih-hero__video-trigger"
                onClick={openVideo}
                aria-label={intl.formatMessage(messages.heroVideoCta)}
              >
                <img
                  className="jiltarjih-hero__video-thumb"
                  src={heroContent.videoThumbnail}
                  alt={intl.formatMessage(messages.heroVideoCta)}
                />
                <span className="jiltarjih-hero__video-caption">
                  <FontAwesomeIcon icon={faPlay} />
                  {intl.formatMessage(messages.heroVideoCta)}
                </span>
              </button>
            </div>
          )}
        </div>
      </Container>
      <HomePromoVideoModalSlot
        isOpen={isVideoOpen}
        close={closeVideo}
        videoId={videoId}
      />
    </section>
  );
};

export const HomeStepsSection = () => {
  const intl = useIntl();

  return (
    <section className="jiltarjih-section jiltarjih-section--steps">
      <Container size="xl">
        <SectionHeading
          eyebrow={intl.formatMessage(messages.stepsEyebrow)}
          title={intl.formatMessage(messages.stepsTitle)}
        />
        <div className="jiltarjih-steps-grid">
          {stepItems.map((item, index) => (
            <article key={item.title.id} className="jiltarjih-step-card">
            <div className="jiltarjih-step-card__icon">
              <span className="jiltarjih-step-card__index">{index + 1}</span>
              <FontAwesomeIcon icon={item.icon} />
            </div>
              <h3>{intl.formatMessage(item.title)}</h3>
              <p>{intl.formatMessage(item.text)}</p>
            </article>
          ))}
        </div>
      </Container>
    </section>
  );
};

export const HomeRecentCoursesSection = () => {
  const intl = useIntl();
  const navigate = useNavigate();

  return (
    <section className="jiltarjih-section jiltarjih-section--courses">
      <Container size="xl">
        <SectionHeading
          eyebrow={intl.formatMessage(messages.coursesEyebrow)}
          title={intl.formatMessage(messages.coursesTitle)}
        />
        <div className="jiltarjih-recent-courses">
          <HomeCoursesListSlot />
        </div>
        <div className="jiltarjih-section__actions jiltarjih-section__actions--bottom">
          <Button variant="brand" onClick={() => navigate(ROUTES.COURSES)}>
            {intl.formatMessage(messages.coursesCta)}
          </Button>
        </div>
      </Container>
    </section>
  );
};

export const HomeServicesSection = () => {
  const intl = useIntl();

  return (
    <section className="jiltarjih-section jiltarjih-section--services">
      <Container size="xl">
        <SectionHeading
          eyebrow={intl.formatMessage(messages.servicesEyebrow)}
          title={intl.formatMessage(messages.servicesTitle)}
        />
        <div className="jiltarjih-services-grid">
          {serviceItems.map(item => (
            <article key={item.title.id} className="jiltarjih-service-card">
            <div className="jiltarjih-service-card__icon">
              <FontAwesomeIcon icon={item.icon} />
            </div>
              <h3>{intl.formatMessage(item.title)}</h3>
              <p>{intl.formatMessage(item.text)}</p>
            </article>
          ))}
        </div>
      </Container>
    </section>
  );
};

export const HomePartnersSection = () => {
  const intl = useIntl();

  return (
    <section className="jiltarjih-section jiltarjih-section--partners">
      <Container size="xl">
        <SectionHeading
          eyebrow={intl.formatMessage(messages.partnersEyebrow)}
          title={intl.formatMessage(messages.partnersTitle)}
        />
        <div className="jiltarjih-partners-grid">
          {partnerItems.map((partner) => {
            return (
              <a
                key={partner.title.id}
                className="jiltarjih-partner-card"
                href={partner.href}
                target="_blank"
                rel="noreferrer"
              >
                <img src={partner.image} alt={intl.formatMessage(partner.title)} />
              </a>
            );
          })}
        </div>
      </Container>
    </section>
  );
};

export const HomeTestimonialsSection = () => {
  const intl = useIntl();
  const [activeIndex, setActiveIndex] = useState(0);
  const activeItem = testimonialItems[activeIndex];

  useEffect(() => {
    const timerId = window.setInterval(() => {
      setActiveIndex((current) => (current + 1) % testimonialItems.length);
    }, 6000);

    return () => window.clearInterval(timerId);
  }, []);

  const handlePrevious = () => {
    setActiveIndex((activeIndex - 1 + testimonialItems.length) % testimonialItems.length);
  };

  const handleNext = () => {
    setActiveIndex((activeIndex + 1) % testimonialItems.length);
  };

  return (
    <section className="jiltarjih-section jiltarjih-section--testimonials">
      <Container size="xl">
        <SectionHeading
          eyebrow={intl.formatMessage(messages.testimonialsEyebrow)}
          title={intl.formatMessage(messages.testimonialsTitle)}
        />
        <div className="jiltarjih-testimonials-carousel">
          <article key={activeItem.name.id} className="jiltarjih-testimonial-card">
            <span className="jiltarjih-testimonial-card__quote-mark" aria-hidden="true">
              <FontAwesomeIcon icon={faQuoteLeft} />
            </span>
            <div className="jiltarjih-testimonial-card__header">
              <img src={activeItem.image} alt={intl.formatMessage(activeItem.name)} />
              <div>
                <h3>{intl.formatMessage(activeItem.name)}</h3>
                <p>{intl.formatMessage(activeItem.role)}</p>
              </div>
            </div>
            <p className="jiltarjih-testimonial-card__quote">{intl.formatMessage(activeItem.text)}</p>
          </article>
          <div className="jiltarjih-testimonials-carousel__controls">
            <Button
              variant="tertiary"
              onClick={handlePrevious}
              aria-label={intl.formatMessage(messages.testimonialsPrev)}
            >
              <span className="jiltarjih-testimonials-carousel__button">
                <FontAwesomeIcon icon={faArrowRight} />
              </span>
            </Button>
            <div className="jiltarjih-testimonials-carousel__dots" aria-label={intl.formatMessage(messages.testimonialsTitle)}>
              {testimonialItems.map((item, index) => (
                <button
                  key={item.name.id}
                  type="button"
                  className={`jiltarjih-testimonials-carousel__dot${index === activeIndex ? ' is-active' : ''}`}
                  aria-label={intl.formatMessage(item.name)}
                  aria-pressed={index === activeIndex}
                  onClick={() => setActiveIndex(index)}
                />
              ))}
            </div>
            <Button
              variant="tertiary"
              onClick={handleNext}
              aria-label={intl.formatMessage(messages.testimonialsNext)}
            >
              <span className="jiltarjih-testimonials-carousel__button">
                <FontAwesomeIcon icon={faArrowLeft} />
              </span>
            </Button>
          </div>
        </div>
      </Container>
    </section>
  );
};
